import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  formatDate,
  formatPartnerSource,
  getEvaluationMeta,
  normalizeFinancement,
} from '../../../helpers/PartnerHelper';

export const sanitizeFileName = (value) => String(value || 'partenaire')
  .replace(/[\\/:*?"<>|]/g, '_')
  .replace(/\s+/g, '_')
  .substring(0, 60);

export const formatCount = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return numeric.toLocaleString('fr-FR');
};

export const normalizeExportSituation = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'FIN' || normalized === 'FI' || normalized === 'ACHEVÉ' || normalized === 'ACHEVE') {
    return 'Achevé';
  }
  if (normalized === 'RESILIE' || normalized === 'RESILIEE' || normalized === 'RÉSILIÉ') {
    return 'Résilié';
  }
  if (normalized === 'EN COURS') {
    return 'En cours';
  }
  return value || '';
};

export const exportPartnerEvaluationExcel = (detail, generatedDate) => {
  const workbook = XLSX.utils.book_new();
  const summaryRows = [
    ['FICHE D\'EVALUATION PARTENAIRE'],
    ['Date de generation', new Date().toLocaleString('fr-FR')],
    [],
    ['Partenaire', detail.name],
    ['Statut juridique', detail.statutJuridique],
    ['Direction(s)', detail.dirs.join(', ')],
    ['Situation', normalizeExportSituation(detail.situation)],
    ['Base source', formatPartnerSource(detail.sourceDb)],
    ['NIF', detail.nif || ''],
    ['STAT', detail.stat || ''],
    ['Contact', detail.contact || ''],
    ['Email', detail.email || ''],
    ['Telephone', detail.tel || ''],
    [],
    ['EVALUATION'],
    ['Resultat', detail.evalResult || 'Non renseigne'],
    ['Date', formatDate(detail.evalDate)],
    [],
    ['HISTORIQUE'],
    ['Date', 'Resultat', 'Marche'],
    ...detail.evalHist.map((history) => [
      formatDate(history.date),
      history.result || '',
      history.marche || '',
    ]),
    [],
    ['SYNTHESE'],
    ['Nb marches', detail.nbMarches],
    ['Marches EN COURS', detail.marchesEnCours],
    ['Marches resilies', detail.marchesResilies],
    ['Beneficiaires total', detail.totalBeneficiaires ?? ''],
    ['Beneficiaires EN COURS', detail.beneficiairesEnCours ?? ''],
    ['Personnel', detail.personnel.length],
  ];

  const ficheSheet = XLSX.utils.aoa_to_sheet(summaryRows);
  ficheSheet['!cols'] = [{ wch: 28 }, { wch: 46 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(workbook, ficheSheet, 'Fiche');

  const marketRows = [
    ['N Marche', 'Financement', 'Composante', 'Direction', 'Objet', 'Zone', 'Nb menages', 'Debut', 'Fin', 'Situation'],
    ...detail.marches.map((market) => [
      market.num,
      normalizeFinancement(market.financement),
      market.composante || '',
      market.direction || '',
      market.objet || '',
      market.zone || '',
      market.beneficiaires ?? '',
      formatDate(market.dateDebut),
      formatDate(market.dateFin),
      normalizeExportSituation(market.situation),
    ]),
    ['', '', '', '', '', 'TOTAL BENEFICIAIRES', detail.totalBeneficiaires ?? '', '', '', ''],
  ];
  const marketSheet = XLSX.utils.aoa_to_sheet(marketRows);
  marketSheet['!cols'] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 12 },
    { wch: 42 },
    { wch: 32 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
  ];
  XLSX.utils.book_append_sheet(workbook, marketSheet, 'Marches');

  if (detail.personnel.length) {
    const personnelSheet = XLSX.utils.aoa_to_sheet([
      ['Nom', 'CIN', 'Fonction', 'Email', 'Telephone', 'Debut', 'Fin', 'Situation'],
      ...detail.personnel.map((person) => [
        person.nom,
        person.cin || '',
        person.fonction || '',
        person.email || '',
        person.telephone || '',
        formatDate(person.dateDebut),
        formatDate(person.dateFin),
        normalizeExportSituation(person.situation),
      ]),
    ]);
    personnelSheet['!cols'] = [
      { wch: 30 },
      { wch: 18 },
      { wch: 24 },
      { wch: 32 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(workbook, personnelSheet, 'Personnel');
  }

  XLSX.writeFile(workbook, `Fiche_Eval_${sanitizeFileName(detail.name)}_${generatedDate}.xlsx`);
};

export const exportPartnerEvaluationPdf = (detail, generatedAt, generatedDate) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const evalMeta = getEvaluationMeta(detail.evalResult);
  const evalColor = evalMeta?.sx?.color || '#64748b';

  doc.setFillColor(30, 58, 95);
  doc.roundedRect(10, 10, pageWidth - 20, 34, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(detail.name, 16, 23);
  doc.setFontSize(9);
  doc.text(`${detail.statutJuridique || '-'} | ${detail.dirs.join(' / ') || '-'}`, 16, 30);
  doc.text(`NIF: ${detail.nif || '-'} | STAT: ${detail.stat || '-'} | Situation: ${normalizeExportSituation(detail.situation)}`, 16, 36);
  doc.text(`Base source: ${formatPartnerSource(detail.sourceDb)}`, 16, 41);
  doc.text(`Fiche generee le ${generatedAt}`, pageWidth - 62, 23);

  const summaryY = 52;
  doc.setTextColor(30, 41, 59);
  const summaryCards = [
    ['Evaluation', detail.evalResult || 'Non renseigne', formatDate(detail.evalDate), evalColor],
    ['Marches', String(detail.nbMarches), `${detail.marchesEnCours} EN COURS`, '#2563eb'],
    ['Personnel', String(detail.personnel.length), 'personnes', '#7c3aed'],
  ];

  summaryCards.forEach((card, index) => {
    const x = 10 + (index * ((pageWidth - 20) / summaryCards.length));
    const w = ((pageWidth - 20) / summaryCards.length) - 3;
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, summaryY, w, 22, 2, 2, 'FD');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(card[0], x + 3, summaryY + 6);
    doc.setFontSize(10);
    doc.setTextColor(card[3]);
    doc.text(doc.splitTextToSize(card[1], w - 6), x + 3, summaryY + 12);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(card[2], x + 3, summaryY + 19);
  });

  let yPos = 84;
  if (detail.evalHist.length) {
    doc.setFontSize(10);
    doc.setTextColor(30, 64, 175);
    doc.text('Historique evaluations', 10, yPos);
    doc.autoTable({
      startY: yPos + 3,
      head: [['Date', 'Resultat', 'Marche']],
      body: detail.evalHist.map((history, index) => [
        formatDate(history.date),
        `${history.result || ''}${index === 0 ? ' (DERNIERE)' : ''}`,
        history.marche || '',
      ]),
      theme: 'striped',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 58, 95], textColor: 255 },
      margin: { left: 10, right: 10 },
    });
    yPos = doc.lastAutoTable.finalY + 8;
  }

  doc.setFontSize(10);
  doc.setTextColor(30, 64, 175);
  doc.text(`Contrats / Marches (${detail.marches.length})`, 10, yPos);
  doc.autoTable({
    startY: yPos + 3,
    head: [['N Marche', 'Composante', 'Financement', 'Direction', 'Nb menages', 'Debut', 'Fin', 'Situation']],
    body: [
      ...detail.marches.map((market) => [
        market.num || '',
        market.composante || '',
        normalizeFinancement(market.financement),
        market.direction || '',
        formatCount(market.beneficiaires),
        formatDate(market.dateDebut),
        formatDate(market.dateFin),
        normalizeExportSituation(market.situation),
      ]),
      ['TOTAL BENEFICIAIRES', '', '', '', formatCount(detail.totalBeneficiaires), '', '', ''],
    ],
    theme: 'striped',
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [30, 58, 95], textColor: 255 },
    margin: { left: 10, right: 10 },
  });

  if (detail.personnel.length) {
    doc.addPage('a4', 'landscape');
    doc.setFontSize(12);
    doc.setTextColor(30, 64, 175);
    doc.text(`Personnel (${detail.personnel.length})`, 10, 14);
    doc.autoTable({
      startY: 18,
      head: [['Nom', 'CIN', 'Fonction', 'Email', 'Telephone', 'Debut', 'Fin', 'Situation']],
      body: detail.personnel.map((person) => [
        person.nom || '',
        person.cin || '',
        person.fonction || '',
        person.email || '',
        person.telephone || '',
        formatDate(person.dateDebut),
        formatDate(person.dateFin),
        normalizeExportSituation(person.situation),
      ]),
      theme: 'striped',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 58, 95], textColor: 255 },
      margin: { left: 10, right: 10 },
    });
  }

  doc.save(`Fiche_Eval_${sanitizeFileName(detail.name)}_${generatedDate}.pdf`);
};
